import React from "react";
import { motion } from "framer-motion";
import { Phone, ChevronRight, ShieldCheck, Star, MapPin } from "lucide-react";
import { scrollToId } from "../lib/api";
import { CONTACT } from "../data/content";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.16, 1, 0.3, 1] } }),
};

const HERO_IMG = "/plumber-midage.jpg";

export default function Hero() {
  return (
    <section id="top" className="relative min-h-[100svh] flex items-center overflow-hidden bg-slate-950 ss-grain">
      {/* bg layers */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-sky-950 to-slate-900" />
        <div className="absolute inset-0 ss-hero-grid opacity-60" />
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-sky-500/20 blur-[120px]" />
        <div className="absolute bottom-0 -left-32 w-[500px] h-[500px] rounded-full bg-cyan-500/10 blur-[120px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 items-center pt-28 pb-16">
        {/* copy */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 backdrop-blur px-4 py-1.5 text-xs font-bold tracking-wide text-sky-200 mb-6"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-400" />
            </span>
            BUDAPEST &amp; KESZTHELY KÖRNYÉKE
          </motion.div>

          <h1 className="font-display font-extrabold text-white tracking-tight text-4xl sm:text-5xl lg:text-[68px] leading-[1.03]">
            {["Víz- és fűtésszerelés", "tisztán, pontosan,", "korrekt áron."].map((t, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block">
                  {i === 2 ? (
                    <span className="bg-gradient-to-r from-sky-400 to-cyan-300 bg-clip-text text-transparent">{t}</span>
                  ) : (
                    t
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-6 max-w-xl text-lg text-slate-300 leading-relaxed"
          >
            Csöpög a csap? Elromlott a WC-tartály? Fűtési gond? A <strong className="text-white">Szuper Szaki</strong> megbízhatóan megoldja – precíz munkavégzéssel és garanciával.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.85 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <button
              data-testid="hero-cta-quote"
              onClick={() => scrollToId("arajanlat")}
              className="group inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-white px-7 py-4 rounded-full font-bold text-base shadow-xl shadow-sky-500/30 transition-colors"
            >
              Ingyenes árajánlatot kérek
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <a
              data-testid="hero-cta-call"
              href={`tel:${CONTACT.phoneHref}`}
              className="inline-flex items-center gap-2 border border-white/20 hover:bg-white/10 text-white px-7 py-4 rounded-full font-bold text-base backdrop-blur transition-colors"
            >
              <Phone className="w-5 h-5" /> {CONTACT.phone}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-300"
          >
            <span className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-cyan-400" /> 15+ év tapasztalat</span>
            <span className="inline-flex items-center gap-2"><Star className="w-4 h-4 text-amber-400" /> 100% elégedettség</span>
            <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4 text-sky-400" /> Gyors kiszállás</span>
          </motion.div>
        </div>

        {/* image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5 relative"
        >
          <div className="relative rounded-[28px] overflow-hidden border border-white/10 shadow-2xl">
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 to-transparent z-10" />
            <img src={HERO_IMG} alt="Szuper Szaki – Kálmán Fülöp víz- és fűtésszerelő mester" className="w-full h-[440px] lg:h-[560px] object-cover object-top" />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.1 }}
            className="absolute -bottom-6 left-5 z-20 bg-white rounded-2xl shadow-2xl px-5 py-4 flex items-center gap-3 ring-1 ring-slate-100 whitespace-nowrap"
          >
            <div className="w-11 h-11 rounded-xl bg-sky-100 flex items-center justify-center shrink-0">
              <ShieldCheck className="w-6 h-6 text-sky-600" />
            </div>
            <div className="leading-tight">
              <div className="font-display font-extrabold text-slate-900 text-base">Garancia</div>
              <div className="text-xs text-slate-500">minden elvégzett munkára</div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
